import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { getOrgLocale } from '../../../lib/locale';
import PricingSection from './PricingSection';
import TypeFields from './TypeFields';
import { typeOf } from './types';

const EMPTY = {
  name: '', sku: '', barcode: '', description: '', category: '', brand: '', unit: '', location: '',
  quantity_on_hand: '', reorder_level: '', reorder_qty: '', max_level: '', cost_price: '', sell_price: '',
  tax_rate: '', track_expiry: false, expiry_date: '', batch_no: '', status: 'Active', notes: '', suppliers: [],
  pack_name: '', units_per_pack: '', pack_cost: '', sell_by: 'unit', unit_size_ml: '', portion_ml: '', portion_name: '',
  mixer_item_id: null, mixer_qty: '', mixer_cost: '', sell_inc_tax: true, sell_price_pack: '', min_margin_pct: '',
};
const NUMS = ['quantity_on_hand', 'reorder_level', 'reorder_qty', 'max_level', 'cost_price', 'sell_price', 'tax_rate', 'units_per_pack', 'pack_cost', 'unit_size_ml', 'portion_ml', 'mixer_qty', 'mixer_cost', 'sell_price_pack', 'min_margin_pct', 'yield_pct', 'wastage_pct', 'pallet_qty', 'weight_kg', 'length_cm', 'width_cm', 'height_cm'];
const SUP_NUMS = ['price', 'pack_size', 'min_order_qty', 'lead_time_days'];
const toNum = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
const PACK_WORDS = ['case', 'carton', 'slab', 'box', 'pack', 'crate', 'tray', 'pallet'];

const F = ({ label, children }) => <div><Label className="text-xs">{label}</Label>{children}</div>;

export default function ItemFormDialog({ open, onOpenChange, item, suppliers, lists, onSave, items = [] }) {
  const [f, setF] = useState(EMPTY);
  const invType = getOrgLocale().inventory_type;
  const t = typeOf(invType);
  useEffect(() => {
    if (open) { setF(item ? { ...EMPTY, ...item, suppliers: item.suppliers || [], cost_inc_tax: item.cost_inc_tax ?? getOrgLocale().po_prices_include_tax } : { ...EMPTY, track_expiry: !!t.expiryDefault, cost_inc_tax: getOrgLocale().po_prices_include_tax }); }
  }, [open, item, t.expiryDefault]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setSup = (i, k, v) => setF({ ...f, suppliers: f.suppliers.map((s, j) => (j === i ? { ...s, [k]: v } : (k === 'preferred' && v ? { ...s, preferred: false } : s))) });
  const inp = (k, props = {}) => <Input value={f[k] ?? ''} onChange={set(k)} data-testid={`item-${k}`} {...props} />;

  const save = () => {
    if (!f.name.trim()) { toast.error('Give the item a name'); return; }
    const body = { ...f };
    NUMS.forEach((k) => { body[k] = toNum(body[k]); });
    body.quantity_on_hand = body.quantity_on_hand || 0;
    if (body.pack_cost != null && body.units_per_pack) { body.cost_price = Math.round((body.pack_cost / body.units_per_pack) * 10000) / 10000; }
    body.suppliers = f.suppliers.filter((s) => s.supplier_id).map((s) => {
      const o = { ...s }; SUP_NUMS.forEach((k) => { o[k] = toNum(o[k]); }); return o;
    });
    ['id', 'on_order', 'backorder', 'low_stock', 'stock_value', 'created_at', 'updated_at', 'created_by', 'updated_by'].forEach((k) => delete body[k]);
    onSave(body);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="item-form-dialog">
        <DialogHeader>
          <DialogTitle>{item ? 'Edit item' : 'New inventory item'}</DialogTitle>
          <DialogDescription>Only the name is required — fill in what is useful for your business.</DialogDescription>
        </DialogHeader>
        <datalist id="gi-cats">{lists.categories.map((c) => <option key={c} value={c} />)}</datalist>
        <datalist id="gi-locs">{lists.locations.map((c) => <option key={c} value={c} />)}</datalist>
        <datalist id="gi-units">{[...new Set([...lists.units, 'each', 'box', 'carton', 'bottle', 'case', 'kg', 'L', 'm', 'pack', 'roll'])].map((c) => <option key={c} value={c} />)}</datalist>

        <section className="space-y-2">
          <h4 className="text-sm font-semibold text-slate-700">Item</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2"><F label="Name *">{inp('name', { placeholder: 'e.g. Shiraz 750ml, Floor cleaner 5L' })}</F></div>
            <F label="SKU / item code">{inp('sku')}</F>
            <F label="Barcode">{inp('barcode')}</F>
            <F label="Category">{inp('category', { list: 'gi-cats', placeholder: 'e.g. Wine, Chemicals' })}</F>
            <F label="Brand">{inp('brand')}</F>
            <F label="Single item unit">{inp('unit', { list: 'gi-units', placeholder: 'bottle, can, kg' })}
              {PACK_WORDS.includes((f.unit || '').trim().toLowerCase()) && <p className="text-[11px] text-amber-700 mt-1" data-testid="item-unit-warning">This is the single item (e.g. bottle or can). Put &quot;{f.unit}&quot; in Pack name under Buying.</p>}
            </F>
            <F label="Status">
              <select className="border rounded-md h-10 px-2 w-full text-sm" value={f.status || 'Active'} onChange={set('status')} data-testid="item-status">
                <option>Active</option><option>Inactive</option>
              </select>
            </F>
            <div className="md:col-span-4"><F label="Description"><Textarea rows={2} value={f.description || ''} onChange={set('description')} /></F></div>
          </div>
        </section>

        <section className="space-y-2">
          <h4 className="text-sm font-semibold text-slate-700">Stock & reorder</h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <F label="On hand">{inp('quantity_on_hand', { type: 'number', step: 'any' })}</F>
            <F label="Location">{inp('location', { list: 'gi-locs', placeholder: 'Cellar, Store room' })}</F>
            <F label="Reorder level">{inp('reorder_level', { type: 'number', step: 'any' })}</F>
            <F label="Reorder qty">{inp('reorder_qty', { type: 'number', step: 'any' })}</F>
            <F label="Max level (par)">{inp('max_level', { type: 'number', step: 'any' })}</F>
          </div>
        </section>

        <PricingSection f={f} setF={setF} items={items} allowPour={t.pour} />
        <TypeFields type={invType} f={f} setF={setF} />

        <section className="space-y-2">
          <h4 className="text-sm font-semibold text-slate-700">Tax & expiry</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <F label={`${getOrgLocale().tax_name} %`}>{inp('tax_rate', { type: 'number', step: 'any', placeholder: String(getOrgLocale().tax_rate) })}</F>
            <label className="flex items-end gap-2 text-sm pb-2"><Checkbox checked={!!f.track_expiry} onCheckedChange={(v) => setF({ ...f, track_expiry: !!v })} data-testid="item-track-expiry" /> Track expiry / batch</label>
            {f.track_expiry && <F label="Expiry date">{inp('expiry_date', { type: 'date' })}</F>}
            {f.track_expiry && <F label="Batch / lot">{inp('batch_no')}</F>}
          </div>
        </section>

        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-slate-700">Suppliers</h4>
            <Button size="sm" variant="outline" onClick={() => setF({ ...f, suppliers: [...f.suppliers, { supplier_id: '', preferred: f.suppliers.length === 0 }] })} data-testid="item-add-supplier"><Plus className="h-3 w-3 mr-1" /> Add supplier</Button>
          </div>
          {f.suppliers.length === 0 && <p className="text-xs text-slate-500">Add the suppliers you buy this from, with their code and price — used when raising purchase orders.</p>}
          {f.suppliers.map((s, i) => (
            <div key={i} className="grid grid-cols-2 md:grid-cols-8 gap-2 items-end border rounded-md p-2" data-testid={`item-supplier-${i}`}>
              <div className="col-span-2">
                <Label className="text-xs">Supplier</Label>
                <select className="border rounded-md h-9 px-2 w-full text-sm" value={s.supplier_id || ''} onChange={(e) => setSup(i, 'supplier_id', e.target.value)} data-testid={`item-supplier-select-${i}`}>
                  <option value="">Select…</option>
                  {suppliers.map((x) => <option key={x.id} value={x.id}>{x.company_name}</option>)}
                </select>
              </div>
              <F label="Their code"><Input className="h-9" value={s.supplier_item_code || ''} onChange={(e) => setSup(i, 'supplier_item_code', e.target.value)} /></F>
              <F label={`Price (${f.cost_inc_tax ? 'inc' : 'ex'} ${getOrgLocale().tax_name})`}><Input className="h-9" type="number" step="0.01" value={s.price ?? ''} onChange={(e) => setSup(i, 'price', e.target.value)} data-testid={`item-supplier-price-${i}`} /></F>
              <F label="Order unit"><Input className="h-9" value={s.order_unit || ''} onChange={(e) => setSup(i, 'order_unit', e.target.value)} placeholder="case" /></F>
              <F label="Min order"><Input className="h-9" type="number" step="any" value={s.min_order_qty ?? ''} onChange={(e) => setSup(i, 'min_order_qty', e.target.value)} /></F>
              <F label="Lead days"><Input className="h-9" type="number" value={s.lead_time_days ?? ''} onChange={(e) => setSup(i, 'lead_time_days', e.target.value)} /></F>
              <div className="flex items-center gap-2 pb-2">
                <label className="flex items-center gap-1 text-xs"><Checkbox checked={!!s.preferred} onCheckedChange={(v) => setSup(i, 'preferred', !!v)} /> Preferred</label>
                <Button size="sm" variant="ghost" onClick={() => setF({ ...f, suppliers: f.suppliers.filter((_, j) => j !== i) })}><X className="h-4 w-4" /></Button>
              </div>
              {s.last_price != null && <p className="col-span-full text-[11px] text-slate-500">Last paid ${Number(s.last_price).toFixed(2)}</p>}
            </div>
          ))}
        </section>

        <F label="Notes"><Textarea rows={2} value={f.notes || ''} onChange={set('notes')} /></F>
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="flex-1 bg-slate-900" onClick={save} data-testid="item-save">Save item</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
